import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  EntityStatus,
  EntityType,
  IEntityAncestors,
  IOrganisation,
  ITokenScope,
} from '@portal/entity-services/interfaces';
import { DeprecatedModalComponent } from '@portal/shared/ui/modal/src';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';
import { OrganisationService } from '../../../services/organisation.service';
import { TokenScopeService } from './token-scope.service';

@Component({
    selector: 'portal-scope-token',
    templateUrl: './token-scope.component.html',
    styleUrls: ['./token-scope.component.scss'],
    standalone: false
})
export class TokenScopeComponent implements OnInit, OnDestroy {
  @Input() organisation: IOrganisation;

  @ViewChild('confirmModal') confirmModal: DeprecatedModalComponent;
  editModeState: Set<ITokenScope['tokenScopeUid']> = new Set();
  isCreateMode = false;
  saving = false;
  tokenScopes$: Observable<ITokenScope[]> = this.tokenScopeService.currentEntityTokenScopes$;
  unlinkCallback: () => void;
  deleteTokenScopeCallback: () => void;

  confirmActionType: 'unlinkToken' | 'deleteToken';

  constructor(
    private tokenScopeService: TokenScopeService,
    private organisationService: OrganisationService,
  ) {}

  canAddTokenScope(): boolean {
    return (this.isMerchantCompany || this.isMerchantCompanyGroup) && this.isActive;
  }

  get isMerchantCompany(): boolean {
    return this.organisation?.entityType === EntityType.MERCHANT_COMPANY;
  }

  get isMerchantCompanyGroup(): boolean {
    return this.organisation?.entityType === EntityType.MERCHANT_COMPANY_GROUP;
  }

  get isActive(): boolean {
    return this.organisation?.status === EntityStatus.Active;
  }

  ngOnInit(): void {
    if (this.isActive) {
      if (this.isMerchantCompany || this.isMerchantCompanyGroup) {
        this.tokenScopeService.getAll(this.organisation.entityUid, true).subscribe();
      }

      if (this.organisation?.entityType) {
        this.organisationService
          .getAncestors(this.organisation.entityUid)
          .pipe(
            mergeMap((ancestors: IEntityAncestors) => {
              const merchantCompany = this.organisationService.getMerchantCompany(ancestors);
              const merchantCompanyGroup =
                this.organisationService.getMerchantCompanyGroup(ancestors);
              if (merchantCompany || merchantCompanyGroup) {
                const combinedTokenScopes = [];
                if (merchantCompanyGroup) {
                  const tokenScopesCompanyGroup = this.tokenScopeService.getAll(
                    merchantCompanyGroup.entityUid,
                  );
                  if (tokenScopesCompanyGroup) combinedTokenScopes.push(tokenScopesCompanyGroup);
                }
                if (merchantCompany) {
                  const tokenScopesCompany = this.tokenScopeService.getAll(
                    merchantCompany.entityUid,
                  );
                  if (tokenScopesCompany) combinedTokenScopes.push(tokenScopesCompany);
                }
                return combinedTokenScopes.length > 0 ? forkJoin(combinedTokenScopes) : of([]);
              } else {
                return of([]);
              }
            }),
          )
          .subscribe();
      }
    }
  }

  ngOnDestroy(): void {
    this.tokenScopeService.clearAll();
  }

  onModalConfirmed(confirmed: boolean): void {
    if (confirmed) {
      if (this.confirmActionType === 'unlinkToken') {
        this.unlinkCallback();
      } else if (this.confirmActionType === 'deleteToken') {
        this.deleteTokenScopeCallback();
      }
    }
  }

  unlink(callback: () => void): void {
    this.unlinkCallback = callback;
    this.confirmActionType = 'unlinkToken';
    this.confirmModal.open();
  }

  onClose(tokenScopeUid?: ITokenScope['tokenScopeUid']): void {
    this.isCreateMode = false;
    this.editModeState.delete(tokenScopeUid);
  }

  onSave(tokenScopes: ITokenScope): void {
    this.saving = true;
    this.tokenScopeService
      .addTokenScope(this.organisation.entityUid, tokenScopes)
      .pipe(
        finalize(() => {
          this.onClose();
          this.saving = false;
        }),
      )
      .subscribe();
  }

  onEdit(tokenScopeUid: string): void {
    this.editModeState.add(tokenScopeUid);
    this.isCreateMode = false;
  }

  onDelete(tokenScope: ITokenScope): void {
    this.deleteTokenScopeCallback = () =>
      this.tokenScopeService.deleteTokenScope(tokenScope.tokenScopeUid).subscribe();
    this.confirmActionType = 'deleteToken';
    this.confirmModal.open();
  }

  onCreate(): void {
    this.isCreateMode = true;
    this.editModeState.clear();
  }

  trackById(index: number, item: ITokenScope): ITokenScope['tokenScopeUid'] {
    return item.tokenScopeUid;
  }

  isOrganisationTypeStore(): boolean {
    return (
      this.organisation.entityType === EntityType.UNDEFINED ||
      this.organisation.entityType === EntityType.MERCHANT_SITE
    );
  }
}
